import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { useDispatch } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import {
  callBarging,
  callMonitor,
  callWhisper,
  userLogout
} from '../../../store/slices/tenantSlices/realtimeReportSlice';
import UserLogoutConfirmationModal from './UserLogoutConfirmationModal';
import { useUserDetails } from '../../../store/slices/authSlice';
import { setActiveCallName, setCallId, useCallId } from '../../../store/slices/dialSlice';
import { convertToMask } from '../../../utils/common';
import {
  markAsRead,
  setActiveChatDetails,
  setSupervisorMsgList,
  useSupervisorMsgList
} from '../../../store/slices/tenantSlices/chatSlice';

const ReportList = (props) => {
  const { data = [], setRefresh, onSelectProcess, emptyMessage = 'No data available' } = props;

  const user = useUserDetails();
  const callId = useCallId();
  const dispatch = useDispatch();
  const supervisorMsgList = useSupervisorMsgList();

  const [logOutId, setLogOutId] = useState(null);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(false);

  const typeMapping = {
    0: { title: 'Idle', color: '#2cabe3' },
    1: { title: 'On Call', color: '#008000' },
    2: { title: 'Wrap Up', color: '#775dd0' },
    4: { title: 'On Call', color: '#008000' },
    5: { title: 'Break', color: '#ffb22b' },
    6: { title: 'Dialing', color: '#800080' },
    7: { title: 'Ringing', color: '#6376ff' },
    8: { title: 'Dialing', color: '#800080' },
    9: { title: 'Unregister', color: '#ff9260' },
    10: { title: 'PBX Play', color: '#8e44ad' }
  };

  const onLogoutClick = async () => {
    try {
      const payload = { user_id: logOutId };
      const response = await dispatch(userLogout(payload)).unwrap();
      if (response && response.success) {
        setRefresh(Math.random());
        setLogOutId(null);
        setUsername(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onCallBarging = async (id, Username) => {
    try {
      setLoading(true);
      const payload = { user_id: id };
      const response = await dispatch(callBarging(payload)).unwrap();
      if (response) {
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setActiveCallName(Username));
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log('error', error);
    }
  };

  const onCallMonitor = async (id, Username) => {
    setLoading(true);
    try {
      const payload = { user_id: id };
      const response = await dispatch(callMonitor(payload)).unwrap();
      if (response) {
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setActiveCallName(Username));
        setLoading(false);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log('error', error);
    }
  };

  const onCallWhisper = async (id, Username) => {
    setLoading(true);
    try {
      const payload = { user_id: id };
      const response = await dispatch(callWhisper(payload)).unwrap();
      if (response) {
        dispatch(setCallId(response?.data?.origination_uuid));
        dispatch(setActiveCallName(Username));
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log('error', error);
    }
  };

  const onMarkAsRead = async (row) => {
    try {
      const payload = {
        toUserId: user?.supervisorId,
        toRole: 'supervisor',
        fromUserId: row.user_id,
        fromRole: 'agent'
      };
      await dispatch(markAsRead(payload)).unwrap();
      dispatch(setActiveChatDetails(row));
      dispatch(setSupervisorMsgList({ id: row.user_id, is_delete: true }));
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div className={`report-list-table-wrapper${data.length === 0 ? ' is-empty' : ''}`}>
      <table className="report-list-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Agent Name</th>
            <th>Process Name</th>
            <th>Process Type</th>
            <th>Phone No</th>
            <th>User Type</th>
            {/* <th>Date and Time</th> */}
            {/* <th>Break Name</th> */}
            <th>Duration</th>
            <th>Total Calls</th>
            <th className="text-center">Logout</th>
            {user?.type === 'supervisor' && <th className="text-center">Chat</th>}
            {user?.type === 'supervisor' && <th className="text-center">Action</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const userType = Number(row.user_type);
            const bgColor = typeMapping[userType]?.color || '#ffffff';
            const title =
              userType === 5
                ? `${typeMapping[userType]?.title} - ${row.break_name}`
                : userType === 0 && Number(row.pause_mode === 1)
                  ? `${typeMapping[userType]?.title} - Pause`
                  : userType === 4
                    ? `${typeMapping[userType]?.title} - In`
                    : userType === 1
                      ? `${typeMapping[userType]?.title} - Out`
                      : typeMapping[userType]?.title;
            return (
              <tr key={index} style={{ backgroundColor: `${bgColor}66`, fontWeight: 'bold' }}>
                <td>{row.Username}</td>
                <td>{row.agent_name || '-'}</td>
                <td
                  className="text-decoration-underline cursor-pointer"
                  onClick={() => onSelectProcess?.(row?.processid)}>
                  {row.process_name}
                </td>
                <td>{row.process_type}</td>
                <td>
                  {process.env.REACT_APP_GLOBAL_NUMBER_MASKING === 'true'
                    ? convertToMask(row.phone_number || '-')
                    : row.phone_number || '-'}
                </td>
                <td>{title}</td>
                {/* <td>{new Date(row.date_and_time).toLocaleString()}</td> */}
                {/* <td>{row.break_name}</td> */}
                <td>{row.time_difference}</td>
                <td>{row.total_calls}</td>
                <td className="text-center">
                  <Icon.Power
                    size={16}
                    className="text-danger cursor-pointer"
                    onClick={() => {
                      setLogOutId(row.user_id || '');
                      setUsername(row.Username);
                    }}
                  />
                </td>
                {user?.type === 'supervisor' && (
                  <td className="text-center position-relative">
                    {supervisorMsgList?.includes(row.user_id) && (
                      <div className="bg-danger msg-notification"></div>
                    )}
                    <Icon.MessageCircle
                      size={16}
                      className="text-violet cursor-pointer"
                      onClick={() => {
                        onMarkAsRead(row);
                      }}
                    />
                  </td>
                )}
                {user?.type === 'supervisor' && (
                  <td>
                    {userType === 1 || userType === 4 ? (
                      <div className="d-flex justify-content-around">
                        <span
                          data-tooltip-id={`tooltip-monitor-${row.user_id}`}
                          data-tooltip-content="Call Monitor"
                          className="cursor-pointer">
                          <Icon.Monitor
                            size={16}
                            className="text-info"
                            onClick={() => {
                              if (loading || callId) {
                                return false;
                              }
                              onCallMonitor(row.user_id, row.Username);
                            }}
                          />
                        </span>
                        <Tooltip id={`tooltip-monitor-${row.user_id}`} place="top" />
                        <span
                          data-tooltip-id={`tooltip-barge-${row.user_id}`}
                          data-tooltip-content="Call Barge"
                          className="cursor-pointer">
                          <Icon.Mic
                            size={16}
                            className="text-roseRed"
                            onClick={() => {
                              if (loading || callId) {
                                return false;
                              }
                              onCallBarging(row.user_id, row.Username);
                            }}
                          />
                        </span>
                        <Tooltip id={`tooltip-barge-${row.user_id}`} place="top" />

                        <span
                          data-tooltip-id={`tooltip-whisper-${row.user_id}`}
                          data-tooltip-content="Call Whisper"
                          className="cursor-pointer">
                          <Icon.Headphones
                            size={16}
                            className="text-danger"
                            onClick={() => {
                              if (loading || callId) {
                                return false;
                              }
                              onCallWhisper(row.user_id, row.Username);
                            }}
                          />
                        </span>
                        <Tooltip id={`tooltip-whisper-${row.user_id}`} place="top" />
                      </div>
                    ) : (
                      <div className="text-center">-</div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Sibling of the table, not a <td>: cells carry the 1px grid border, and a div can
          be flex-centred in the space the header leaves without a magic height. */}
      {data.length === 0 && <div className="report-list-empty">{emptyMessage}</div>}
      <UserLogoutConfirmationModal
        isOpen={!!logOutId}
        username={username}
        onLogoutClick={() => onLogoutClick()}
        onCloseClick={() => {
          setLogOutId(null);
          setUsername(null);
        }}
      />
    </div>
  );
};

ReportList.propTypes = {
  data: PropTypes.array.isRequired,
  setRefresh: PropTypes.func,
  onSelectProcess: PropTypes.func,
  emptyMessage: PropTypes.string
};

export default ReportList;
